import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import { BiCalculator } from 'react-icons/bi';
import * as styles from './styles';
import { calculatePaymentDueDate, getTodayLocalDate, PaymentDueDateUnit } from './paymentDueDateCalculator';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (dueDate: string) => void;
}

const PaymentDueDateCalculatorModal: React.FC<IProps> = ({ isOpen, onClose, onConfirm }) => {
    const [startDate, setStartDate] = useState(getTodayLocalDate());
    const [collectionTerm, setCollectionTerm] = useState('30');
    const [unit, setUnit] = useState<PaymentDueDateUnit>('banking');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setStartDate(getTodayLocalDate());
            setCollectionTerm('30');
            setUnit('banking');
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const handleConfirm = (): void => {
        try {
            const term = Number(collectionTerm);
            onConfirm(calculatePaymentDueDate(startDate, term, unit));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Unable to calculate the payment due date.');
        }
    };

    return (
        <div style={modalOverlayStyle} onClick={onClose} role="presentation">
            <div
                style={modalStyle}
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="payment-due-date-title"
            >
                <div style={headerStyle}>
                    <h2 id="payment-due-date-title" style={titleStyle}>
                        <BiCalculator aria-hidden="true" />
                        Calculate Payment Due Date
                    </h2>
                    <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Close payment due date calculator">
                        &times;
                    </button>
                </div>
                <div style={bodyStyle}>
                    <label style={labelStyle}>
                        Start Date
                        <LeaseRecordInput
                            type="date"
                            value={startDate}
                            onChange={(event) => setStartDate(event.target.value)}
                            style={styles.inputBase}
                        />
                    </label>
                    <label style={labelStyle}>
                        Collection Term
                        <LeaseRecordInput
                            type="number"
                            min="0"
                            step="1"
                            value={collectionTerm}
                            onChange={(event) => setCollectionTerm(event.target.value)}
                            style={styles.inputBase}
                        />
                    </label>
                    <label style={labelStyle}>
                        Unit
                        <select value={unit} onChange={(event) => setUnit(event.target.value as PaymentDueDateUnit)} style={styles.selectBase}>
                            <option value="calendar">Calendar</option>
                            <option value="banking">Banking</option>
                        </select>
                    </label>
                    <div style={hintStyle}>Banking days exclude Saturdays and Sundays.</div>
                    {error && (
                        <div role="alert" style={errorStyle}>
                            {error}
                        </div>
                    )}
                </div>
                <div style={footerStyle}>
                    <button type="button" onClick={onClose} style={styles.buttonSecondary}>
                        Cancel
                    </button>
                    <button type="button" onClick={handleConfirm} style={styles.buttonPrimary}>
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
};

const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1100,
    backgroundColor: 'rgba(17, 24, 39, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
};
const modalStyle: React.CSSProperties = {
    width: 'min(460px, 100%)',
    backgroundColor: styles.colors.white,
    borderRadius: '8px',
    boxShadow: styles.shadows.xl,
    border: `1px solid ${styles.colors.gray300}`,
};
const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.9rem 1rem',
    borderBottom: `1px solid ${styles.colors.gray200}`,
};
const titleStyle: React.CSSProperties = {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: styles.colors.gray900,
    fontSize: styles.typography.fontSize.lg,
};
const closeButtonStyle: React.CSSProperties = {
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    color: styles.colors.gray600,
    fontSize: '1.75rem',
    lineHeight: 1,
};
const bodyStyle: React.CSSProperties = { padding: '1rem', display: 'grid', gap: '0.85rem' };
const labelStyle: React.CSSProperties = {
    ...styles.fieldLabel,
    display: 'grid',
    gap: '0.25rem',
};
const hintStyle: React.CSSProperties = { color: styles.colors.gray600, fontSize: styles.typography.fontSize.xs };
const errorStyle: React.CSSProperties = { color: styles.colors.danger, fontSize: styles.typography.fontSize.xs };
const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    borderTop: `1px solid ${styles.colors.gray200}`,
};

export default PaymentDueDateCalculatorModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/PaymentDueDateCalculatorModal.tsx